import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Step {
  icon: string;
  color: string;
  step: string;
  title: string;
  desc: string;
}

const ConsultingCTA = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const steps: Step[] = [
    {
      icon: 'ri-calendar-check-line',
      color: '#8c52ff',
      step: '01',
      title: t('consulting.process.items.s1.title'),
      desc: t('consulting.process.items.s1.desc'),
    },
    {
      icon: 'ri-search-eye-line',
      color: '#00c2ff',
      step: '02',
      title: t('consulting.process.items.s2.title'),
      desc: t('consulting.process.items.s2.desc'),
    },
    {
      icon: 'ri-road-map-line',
      color: '#FF2DAA',
      step: '03',
      title: t('consulting.process.items.s3.title'),
      desc: t('consulting.process.items.s3.desc'),
    },
    {
      icon: 'ri-rocket-2-line',
      color: '#8c52ff',
      step: '04',
      title: t('consulting.process.items.s4.title'),
      desc: t('consulting.process.items.s4.desc'),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* How It Works */}
      <section className="relative py-24 bg-[#030006] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(0,194,255,0.06)_0%,transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 bg-[#00c2ff]/10 border border-[#00c2ff]/30 rounded-full px-4 py-1.5 text-[#00c2ff] text-xs font-bold uppercase tracking-widest mb-5">
              <i className="ri-route-line" />
              {t('consulting.process.hero.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              {t('consulting.process.hero.titlePrefix')} <span className="bg-gradient-to-r from-[#00c2ff] to-[#8c52ff] bg-clip-text text-transparent">{t('consulting.process.hero.titleHighlight')}</span>
            </h2>
            <p className="text-white/50 text-base max-w-xl mx-auto">
              {t('consulting.process.hero.subtitle')}
            </p>
          </div>

          <div
            ref={ref}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {steps.map((s, i) => (
              <div
                key={s.step}
                className="relative rounded-2xl p-6 border border-white/8 group hover:border-white/20 transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                <div className="text-5xl font-black mb-4 leading-none" style={{ color: `${s.color}20` }}>
                  {s.step}
                </div>
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-4"
                  style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}
                >
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main CTA */}
      <section className="relative py-24 bg-[#050008] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(140,82,255,0.10)_0%,transparent_60%)]" />
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <div className="relative rounded-3xl overflow-hidden border border-[#8c52ff]/20 p-10 md:p-16"
            style={{ background: 'linear-gradient(135deg, rgba(140,82,255,0.08) 0%, rgba(0,194,255,0.08) 100%)' }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8c52ff]/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00c2ff]/40 to-transparent" />

            <div className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-6 bg-[#8c52ff]/15 border border-[#8c52ff]/30">
              <i className="ri-briefcase-4-line text-2xl text-[#8c52ff]" />
            </div>

            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              {t('consulting.cta.titlePrefix')}<br />
              <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                {t('consulting.cta.titleHighlight')}
              </span>
            </h2>
            <p className="text-white/55 text-base leading-relaxed max-w-xl mx-auto mb-8">
              {t('consulting.cta.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
              >
                <i className="ri-calendar-check-line" />
                {t('consulting.cta.cta.book')}
              </Link>
              <Link
                to="/services"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                {t('consulting.cta.cta.services')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#030006] border-t border-white/5 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-start gap-3 bg-white/3 border border-white/8 rounded-xl px-6 py-4">
            <i className="ri-information-line text-white/30 text-lg flex-shrink-0 mt-0.5" />
            <p className="text-white/35 text-xs leading-relaxed text-left">
              <strong className="text-white/50 font-semibold">{t('consulting.disclaimer.title')}:</strong> {t('consulting.disclaimer.text')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConsultingCTA;
